/**
 * New Customers
 *
 * Everyone who bought for the first time, newest first, searchable.
 *
 * Replaced an Order Transactions view that reconciled Manifold's Hyperswitch
 * intents against core orders. That screen could not see prepaid bundles: a
 * bundle's zero-dollar fill orders reference a synthesised `pay_..._bundle_2`
 * that no payment store holds, so each one was reported as an order whose
 * charge had gone missing — 105 such rows on Science.co alone. Here a bundle is
 * ONE row, at the price the customer actually paid.
 *
 * One row is one first purchase, which is the same cohort the Daily Report's
 * New Orders column counts — verified equal on every clinic rather than
 * assumed, because two screens quoting different new-customer counts is worse
 * than either of them being slightly wrong.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { format, parse, subDays } from 'date-fns'
import {
  Loader2, RefreshCw, AlertCircle, Search, X, UserPlus, ExternalLink, Package, Route,
} from 'lucide-react'

import { billingReportsAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { Card, CardContent, Button } from '../components/ui'
import { DateRangePicker } from '../components/DateRangePicker'
import { channelLabel, channelBadgeStyle } from '../lib/channels'

const PAGE_SIZE = 50

/**
 * How a channel is shown, and how the row explains itself.
 *
 * The label alone is worth little: three different registers can produce
 * "Google", and which one did tells you how much to trust it. `price_book` is
 * the funnel stating outright what it sold; `source_name` is the utm_source the
 * landing page received; `click_id` means the funnel labelled nothing and a
 * leftover gclid is the only evidence there is — the rows most worth opening
 * the journey for.
 */
// Labels and badge styles come from lib/channels, so a channel this page has
// no entry for reads as a name rather than as a raw id.

const BASIS_LABELS = {
  price_book: 'price book',
  source_name: 'sources[0]',
  click_id: 'click id',
}

const touchName = touch => touch?.name || 'untagged'

/**
 * The evidence line under the channel — what actually decided it, and who else
 * touched the customer.
 *
 * A customer with more than one source shows the first two, because a second ad
 * on a different channel had a hand in a sale the badge credits to one. The
 * badge does NOT change: it stays first touch, which is what the Daily Report
 * and the traffic report count, so a row reading Meta here is the row reading
 * Meta there.
 *
 * "mixed" is reserved for two touches that genuinely compete — both naming a
 * real channel, and different ones. A tagged first touch followed by an
 * untagged return is the same customer coming back, not a contested sale, and
 * on TMATES that shape is 82 of the 112 customers whose first two touches
 * resolve differently. See services/channelAttribution.touchTrail.
 */
function channelEvidence(row) {
  const touches = row.channel_touches || []
  const more = row.channel_touch_count > 2 ? ` +${row.channel_touch_count - 2} more` : ''

  if (row.channel_mixed && touches.length > 1) {
    return `mixed: ${touchName(touches[0])} → ${touchName(touches[1])}${more}`
  }

  const base = !row.channel_basis
    // `other` with nothing behind it. The detail distinguishes a label we do
    // not bucket from no label at all, which are different things to fix.
    ? (row.channel_detail || 'nothing recorded')
    : (row.channel_detail
        ? `via ${BASIS_LABELS[row.channel_basis] || row.channel_basis}: ${row.channel_detail}`
        : `via ${BASIS_LABELS[row.channel_basis] || row.channel_basis}`)

  return touches.length > 1 ? `${base}, then ${touchName(touches[1])}${more}` : base
}


function formatMoney(cents) {
  if (cents === null || cents === undefined) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(cents / 100)
}

/**
 * A charge timestamp as a local date and time.
 *
 * Shown to the minute because the list is ordered by it: two rows a minute
 * apart look like a tie without one, and this is the screen people open to ask
 * "did that just come through".
 */
function formatWhen(ms) {
  if (!ms) return '—'
  return new Date(ms).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

function getDefaultDateRange() {
  return { from: subDays(new Date(), 29), to: new Date() }
}

export default function NewCustomersPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentClinicId } = useAuthStore()

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)

  // The committed term, in the URL so a search is a shareable link.
  const search = searchParams.get('q') || ''
  // What is in the box right now, which is not the same thing — typing must not
  // fire a request per keystroke against a window-wide scan.
  const [draft, setDraft] = useState(search)
  useEffect(() => { setDraft(search) }, [search])

  const dateRange = useMemo(() => {
    const startParam = searchParams.get('start')
    const endParam = searchParams.get('end')
    if (startParam && endParam) {
      const from = parse(startParam, 'yyyy-MM-dd', new Date())
      const to = parse(endParam, 'yyyy-MM-dd', new Date())
      if (!isNaN(from.getTime()) && !isNaN(to.getTime())) return { from, to }
    }
    return getDefaultDateRange()
  }, [searchParams])

  const setDateRange = useCallback((newRange) => {
    if (!newRange?.from || !newRange?.to) return
    setSearchParams(prev => {
      const params = new URLSearchParams(prev)
      params.set('start', format(newRange.from, 'yyyy-MM-dd'))
      params.set('end', format(newRange.to, 'yyyy-MM-dd'))
      return params
    })
  }, [setSearchParams])

  const commitSearch = useCallback((value) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev)
      if (value) params.set('q', value)
      else params.delete('q')
      return params
    })
  }, [setSearchParams])

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await billingReportsAPI.getNewCustomers({
        startDate: format(dateRange.from, 'yyyy-MM-dd'),
        endDate: format(dateRange.to, 'yyyy-MM-dd'),
        search,
        page,
        limit: PAGE_SIZE,
      })
      setData(response.data.data)
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [dateRange, search, page])

  useEffect(() => { fetchData() }, [fetchData, currentClinicId])
  useEffect(() => { setPage(1) }, [search, dateRange, currentClinicId])

  const rows = data?.rows || []
  const total = data?.total || 0
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold truncate">New Customers</h1>
          <p className="text-xs text-muted-foreground hidden sm:block">
            First purchases, newest first
            {data && ` · ${new Intl.NumberFormat('en-US').format(total)} in range`}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button variant="outline" size="icon" onClick={fetchData} className="h-8 w-8 sm:h-9 sm:w-9">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); commitSearch(draft.trim()) }}
        className="flex items-center gap-2"
      >
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Email, product, funnel, or any payment / order / subscription id"
            className="w-full rounded-lg border border-input bg-background pl-9 pr-9 py-2 text-sm"
          />
          {draft && (
            <button
              type="button"
              onClick={() => { setDraft(''); commitSearch('') }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <Button type="submit" variant="outline" size="sm">Search</Button>
        {search && (
          <span className="text-xs text-muted-foreground">
            {new Intl.NumberFormat('en-US').format(total)} matching “{search}”
          </span>
        )}
      </form>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {data?.truncated && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 text-amber-800 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            This window is large enough that the oldest rows were left out. Narrow the
            dates to see all of them.
          </span>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <UserPlus className="h-8 w-8 mb-2 opacity-40" />
              <p className="text-sm">
                {search ? `Nothing matching “${search}”.` : 'No new customers in this range.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b">
                    <th className="px-4 py-2 font-medium whitespace-nowrap">When</th>
                    <th className="px-4 py-2 font-medium">Customer</th>
                    <th className="px-4 py-2 font-medium">Product</th>
                    <th className="px-4 py-2 font-medium whitespace-nowrap">Funnel</th>
                    <th className="px-4 py-2 font-medium whitespace-nowrap">Channel</th>
                    <th className="px-4 py-2 font-medium text-right whitespace-nowrap">Paid</th>
                    <th className="px-4 py-2 font-medium whitespace-nowrap">Coupon</th>
                    <th className="px-4 py-2 font-medium whitespace-nowrap">Reference</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr
                      key={row.payment_id || row.order_id || `${row.email}-${row.charged_at}`}
                      className="border-b last:border-0 hover:bg-muted/40"
                    >
                      <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground">
                        {formatWhen(row.charged_at)}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="select-all">{row.email || '—'}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="flex items-center gap-1.5">
                          {row.product || '—'}
                          {row.is_bundle && (
                            <span
                              title="Prepaid bundle — charged once up front, shipped over several months"
                              className="inline-flex items-center gap-1 rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-medium text-violet-700"
                            >
                              <Package className="h-2.5 w-2.5" />
                              Bundle
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        {row.intake_slug
                          ? <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">{row.intake_slug}</span>
                          : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <span className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-medium ${channelBadgeStyle(row.channel)}`}>
                          {channelLabel(row.channel)}
                        </span>
                        <span
                          className={`block text-[10px] mt-0.5 ${row.channel_mixed ? 'text-amber-700' : 'text-muted-foreground'}`}
                          title={row.channel_mixed
                            ? 'Two different channels touched this customer. The badge credits the first, which is what every other report counts.'
                            : undefined}
                        >
                          {channelEvidence(row)}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-medium whitespace-nowrap">
                        {formatMoney(row.amount)}
                      </td>
                      {/* Core records no discount, so a 99%-off staff test used
                          to look exactly like a cheap plan. The coupon is read
                          off Manifold's invoice; blank means either no coupon
                          or no Manifold invoice for this charge. */}
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        {row.coupon_code ? (
                          <span
                            className="inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 font-mono text-[11px] font-semibold text-amber-800 dark:text-amber-300"
                            title={row.list_price
                              ? `${formatMoney(row.list_price)} list, ${formatMoney(row.coupon_discount)} off`
                              : undefined}
                          >
                            {row.coupon_code}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                        {row.coupon_code && row.list_price ? (
                          <span className="block text-[10px] text-muted-foreground tabular-nums">
                            was {formatMoney(row.list_price)}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
                        <span className="select-all">{row.payment_id || row.subscription_id || '—'}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {/* Odin addresses an order by customer AND order, so the
                            link only exists when both are known. A bundle base
                            borrows its patient from its own first fill. */}
                        {/* Looked up by EMAIL rather than linked to one
                            anonymous id: the funnel can split a buyer across
                            two ids, and the one that paid is often the one with
                            no ad in it. The lookup shows every id they hold. */}
                        {row.email ? (
                          <Link
                            to={`/reports/user-journey?q=${encodeURIComponent(row.email)}`}
                            className="mr-3 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                            title="Every session and event this customer generated"
                          >
                            Journey <Route className="h-3 w-3" />
                          </Link>
                        ) : null}
                        {row.patient_id && row.order_id ? (
                          <a
                            href={`https://core-odin.5axis.health/dashboard/customers/${row.patient_id}/orders/${row.order_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                          >
                            Odin <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {page} of {lastPage}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= lastPage} onClick={() => setPage(p => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        One row is one customer's first purchase. A prepaid bundle is the single charge
        that bought it, not its individual shipments — so the count here matches the
        Daily Report's New Orders for the same window.
      </p>
    </div>
  )
}
